import { supabase, isSupabaseConfigured } from './supabaseClient'
import {
  ExchangeRateRow,
  RatesData,
  Provider,
  Currency,
} from '../types/calculator'

/**
 * Fetch all active exchange rates from Supabase
 */
export async function fetchRatesFromSupabase(): Promise<RatesData | null> {
  if (!isSupabaseConfigured() || !supabase) {
    return null
  }

  try {
    const { data, error } = await supabase
      .from('exchange_rates')
      .select('*')
      .eq('is_active', true)

    if (error) {
      return null
    }

    if (!data || data.length === 0) {
      return null
    }

    return transformSupabaseData(data as ExchangeRateRow[])
  } catch (error) {
    return null
  }
}

/**
 * Transform Supabase rows into the format used by the app
 */
function transformSupabaseData(rows: ExchangeRateRow[]): RatesData {
  const rates: Record<
    string,
    Record<string, { calculation: number; display: number }>
  > = {}
  const fees: Record<string, number | 'not_defined'> = {}
  let lastUpdated = ''

  rows.forEach((row) => {
    const provider = row.provider
    const currency = row.currency

    // Initialize provider object if it doesn't exist
    if (!rates[provider]) {
      rates[provider] = {}
    }

    // Add rates for this currency
    rates[provider][currency] = {
      calculation: row.calculation_rate,
      display: row.display_rate,
    }

    // Store fee (only need to do this once per provider)
    if (fees[provider] === undefined) {
      fees[provider] = row.fee === null ? 'not_defined' : row.fee
    }

    // Track most recent update
    if (!lastUpdated || row.updated_at > lastUpdated) {
      lastUpdated = row.updated_at
    }
  })

  return {
    rates: rates as Record<
      Provider,
      Record<Currency, { calculation: number; display: number }>
    >,
    fees: fees as Record<Provider, number | 'not_defined'>,
    lastUpdated,
  }
}

/**
 * Subscribe to real-time rate updates
 */
export function subscribeToRateUpdates(
  callback: (data: RatesData | null) => void,
) {
  if (!isSupabaseConfigured() || !supabase) {
    return () => {} // Return empty cleanup function
  }

  const channel = supabase
    .channel('exchange_rates_changes')
    .on(
      'postgres_changes',
      {
        event: '*',
        schema: 'public',
        table: 'exchange_rates',
      },
      async () => {
        // Refetch all rates when any change occurs
        const updatedRates = await fetchRatesFromSupabase()
        callback(updatedRates)
      },
    )
    .subscribe()

  // Return cleanup function
  return () => {
    supabase.removeChannel(channel)
  }
}

/**
 * Seed initial data to Supabase (for first-time setup)
 * Call this once to populate your database with current rates
 */
export async function seedInitialRates() {
  if (!isSupabaseConfigured() || !supabase) {
    return false
  }

  const initialData: Omit<ExchangeRateRow, 'id' | 'updated_at'>[] = [
    // Djamo CI
    {
      provider: 'djamo',
      currency: 'XOF',
      calculation_rate: 1,
      display_rate: 1,
      fee: 200,
      is_active: true,
    },
    {
      provider: 'djamo',
      currency: 'EUR',
      calculation_rate: 682,
      display_rate: 682,
      fee: 200,
      is_active: true,
    },
    {
      provider: 'djamo',
      currency: 'USD',
      calculation_rate: 603,
      display_rate: 603,
      fee: 200,
      is_active: true,
    },

    // Djamo SN
    {
      provider: 'djamosn',
      currency: 'XOF',
      calculation_rate: 1,
      display_rate: 1,
      fee: 200,
      is_active: true,
    },
    {
      provider: 'djamosn',
      currency: 'EUR',
      calculation_rate: 676,
      display_rate: 676,
      fee: 200,
      is_active: true,
    },
    {
      provider: 'djamosn',
      currency: 'USD',
      calculation_rate: 601,
      display_rate: 601,
      fee: 200,
      is_active: true,
    },

    // Wave
    {
      provider: 'wave',
      currency: 'XOF',
      calculation_rate: 1,
      display_rate: 1,
      fee: 0,
      is_active: true,
    },
    {
      provider: 'wave',
      currency: 'EUR',
      calculation_rate: 675.68,
      display_rate: 676,
      fee: 0,
      is_active: true,
    },
    {
      provider: 'wave',
      currency: 'USD',
      calculation_rate: 594.3,
      display_rate: 594,
      fee: 0,
      is_active: true,
    },

    // PushCI
    {
      provider: 'pushci',
      currency: 'XOF',
      calculation_rate: 1,
      display_rate: 1,
      fee: 290,
      is_active: true,
    },
    {
      provider: 'pushci',
      currency: 'EUR',
      calculation_rate: 685,
      display_rate: 685,
      fee: 290,
      is_active: true,
    },
    {
      provider: 'pushci',
      currency: 'USD',
      calculation_rate: 611,
      display_rate: 611,
      fee: 290,
      is_active: true,
    },

    // Orange Money Senegal
    {
      provider: 'orange',
      currency: 'XOF',
      calculation_rate: 1,
      display_rate: 1,
      fee: null,
      is_active: true,
    },
    {
      provider: 'orange',
      currency: 'EUR',
      calculation_rate: 669.754,
      display_rate: 669.754,
      fee: null,
      is_active: true,
    },
    {
      provider: 'orange',
      currency: 'USD',
      calculation_rate: 585.773,
      display_rate: 585.773,
      fee: null,
      is_active: true,
    },

    // Nafolo CI
    {
      provider: 'nafolo',
      currency: 'XOF',
      calculation_rate: 1,
      display_rate: 1,
      fee: 0,
      is_active: true,
    },
    {
      provider: 'nafolo',
      currency: 'EUR',
      calculation_rate: 693,
      display_rate: 693,
      fee: 0,
      is_active: true,
    },
    {
      provider: 'nafolo',
      currency: 'USD',
      calculation_rate: 605,
      display_rate: 605,
      fee: 0,
      is_active: true,
    },
  ]

  try {
    const { error } = await supabase.from('exchange_rates').insert(initialData)

    if (error) {
      return false
    }

    return true
  } catch (error) {
    return false
  }
}
